import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from '@material-tailwind/react';
import {
  ArrowLeftIcon, ArrowUpDownIcon, BringToFrontIcon, DoorOpenIcon, DownloadIcon,
  EraserIcon, FileImageIcon, FileTextIcon, MinusIcon, MousePointer2Icon, PlusIcon,
  Redo2Icon, SaveIcon, SendToBackIcon, ShapesIcon, Undo2Icon,
} from 'lucide-react';
import FloorPlanStage from './canvas-stage';
import {
  AREA_PRESETS, createShapeId, ELEMENT_DEFAULT_STYLE, ENTITY_PLACE_DEFAULTS,
  getWallPoints, LINKABLE_TYPES, snapToGrid, sortShapesByZ, STRUCTURAL_TYPES, wallLength,
} from './constants';
import { getInvalidSeatIds, isSeatOnSharedArea, validateSeatPlacements } from './geometry';
import { exportStageAsPdf, exportStageAsPng } from './export';
import {
  useCreateFloorLayout,
  useGetFloorLayout,
  useGetPlaceableEntities,
  usePublishFloorLayout,
  useSaveFloorLayout,
  useUpdateFloorLayout,
} from '../../custom-hooks/use-floor-layouts';
import { useGetFloorById } from '../../custom-hooks/use-floors';
import { NotifyError } from '../../utils/functions';
import OfficeCrudModal from '../../app/office/crud-modal';
import CoworkingCrudModal from '../../app/coworking/crud-modal';
import SeatCrudModal from '../../app/seat/crud-modal';

/** Only structural extras are free-draw tools. Spaces come from the unplaced list. */
const TOOLS = [
  { id: 'select', label: 'Select', icon: MousePointer2Icon },
  { id: 'wall', label: 'Wall', icon: MinusIcon, elementType: 'wall', shapeType: 'line', w: 120, h: 0 },
  { id: 'door', label: 'Door', icon: DoorOpenIcon, elementType: 'door', shapeType: 'rect', w: 56, h: 56 },
  { id: 'stairs', label: 'Stairs', icon: ArrowUpDownIcon, elementType: 'stairs', shapeType: 'rect', w: 80, h: 60 },
  { id: 'area', label: 'Area', icon: ShapesIcon, elementType: 'area', shapeType: 'rect', w: 100, h: 80, facilityKind: 'washroom', text: 'Washroom' },
];

const MAX_HISTORY = 50;
const MIN_VIEWPORT_H = 360;
const MAX_VIEWPORT_H = 900;

const SECTION_LABELS = {
  offices: 'Offices',
  coworkings: 'Coworking',
  seats: 'Seats',
  meetingRooms: 'Meeting rooms',
};

const Field = ({ label, children, className = '' }) => (
  <label className={`flex items-baseline gap-2 min-w-0 ${className}`}>
    <span className="w-14 shrink-0 text-[11px] uppercase tracking-wide text-gray-400">{label}</span>
    <div className="min-w-0 flex-1">{children}</div>
  </label>
);

const PlainInput = ({ className = '', ...props }) => (
  <input
    {...props}
    className={`w-full min-w-0 bg-transparent border-0 border-b border-gray-200 px-0 py-1 text-sm text-gray-900 outline-none focus:border-gray-500 ${className}`}
  />
);

const PlainSelect = ({ className = '', children, ...props }) => (
  <select
    {...props}
    className={`w-full min-w-0 bg-transparent border-0 border-b border-gray-200 px-0 py-1 text-sm text-gray-900 outline-none focus:border-gray-500 ${className}`}
  >
    {children}
  </select>
);

const ToolBtn = ({ active, disabled, onClick, title, children, danger }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex h-8 items-center gap-1.5 rounded-full px-2.5 text-xs transition-colors disabled:opacity-30 ${danger
      ? 'text-red-600 hover:bg-red-50'
      : active
        ? 'bg-gray-900 text-white'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
  >
    {children}
  </button>
);

const ActionBtn = ({ onClick, disabled, primary, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`h-8 rounded-full px-3 text-xs tracking-wide transition-colors disabled:opacity-40 ${primary
      ? 'bg-gray-900 text-white hover:bg-gray-800'
      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
  >
    {children}
  </button>
);

const FloorPlanEditor = ({ floorId, onBack, onDone }) => {
  const { data: floorRes, isLoading: floorLoading } = useGetFloorById(floorId);
  const floor = floorRes?.data?.floor;

  const { data: layoutPayload, isLoading: layoutLoading, isError: layoutMissing } = useGetFloorLayout(floorId);
  const layout = layoutPayload?.layout;

  const { data: placeableData, refetch: refetchPlaceable } = useGetPlaceableEntities(floorId);

  const createMutation = useCreateFloorLayout();
  const updateMutation = useUpdateFloorLayout();
  const saveMutation = useSaveFloorLayout();
  const publishMutation = usePublishFloorLayout();

  const [shapes, setShapes] = useState([]);
  const [canvas, setCanvas] = useState({ width: 2000, height: 1400, gridSize: 20, useFloorPlanAsBackground: true });
  const [tool, setTool] = useState('select');
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [stageWidth, setStageWidth] = useState(900);
  const [viewportHeight, setViewportHeight] = useState(560);
  const [initialized, setInitialized] = useState(false);
  const [linkingEntity, setLinkingEntity] = useState(null);
  const [areaKind, setAreaKind] = useState('washroom');
  const [officeModalOpen, setOfficeModalOpen] = useState(false);
  const [coworkingModalOpen, setCoworkingModalOpen] = useState(false);
  const [seatModalOpen, setSeatModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [entityListTab, setEntityListTab] = useState('unplaced');

  const autosaveTimer = useRef(null);
  const stageRef = useRef(null);
  const historyIndexRef = useRef(0);
  const historyRef = useRef([[]]);
  const shapesRef = useRef([]);
  const canvasRef = useRef(canvas);
  const layoutExistsRef = useRef(false);

  useEffect(() => { historyIndexRef.current = historyIndex; }, [historyIndex]);
  useEffect(() => { historyRef.current = history; }, [history]);
  useEffect(() => { shapesRef.current = shapes; }, [shapes]);
  useEffect(() => { canvasRef.current = canvas; }, [canvas]);
  useEffect(() => { layoutExistsRef.current = !!layout?._id; }, [layout]);

  useEffect(() => {
    const update = () => setStageWidth(Math.max(480, Math.min(window.innerWidth - 480, 1200)));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    if (layout && !initialized) {
      setShapes(sortShapesByZ(layout.shapes || []));
      setCanvas(layout.canvas || { width: 2000, height: 1400, gridSize: 20, useFloorPlanAsBackground: true });
      setHistory([layout.shapes || []]);
      setHistoryIndex(0);
      setInitialized(true);
    } else if (layoutMissing && !initialized && !layoutLoading) {
      setInitialized(true);
    }
  }, [layout, layoutMissing, layoutLoading, initialized]);

  const scheduleAutosave = useCallback((nextShapes, nextCanvas) => {
    if (!layoutExistsRef.current) return;
    const validation = validateSeatPlacements(nextShapes);
    if (!validation.valid) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      updateMutation.mutate({
        floorId,
        data: { shapes: nextShapes, canvas: nextCanvas },
      });
    }, 1200);
  }, [floorId, updateMutation]);

  const pushHistory = useCallback((nextShapes) => {
    setHistory((prev) => {
      const trimmed = prev.slice(0, historyIndexRef.current + 1);
      const next = [...trimmed, nextShapes].slice(-MAX_HISTORY);
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, []);

  const applyShapes = useCallback((updater, { record = true } = {}) => {
    setShapes((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const sorted = sortShapesByZ(next);
      if (record) {
        pushHistory(sorted);
        scheduleAutosave(sorted, canvasRef.current);
      }
      return sorted;
    });
  }, [pushHistory, scheduleAutosave]);

  const undo = useCallback(() => {
    const idx = historyIndexRef.current;
    if (idx <= 0) return;
    const nextIndex = idx - 1;
    setHistoryIndex(nextIndex);
    setShapes(historyRef.current[nextIndex]);
  }, []);

  const redo = useCallback(() => {
    const idx = historyIndexRef.current;
    if (idx >= historyRef.current.length - 1) return;
    const nextIndex = idx + 1;
    setHistoryIndex(nextIndex);
    setShapes(historyRef.current[nextIndex]);
  }, []);

  const bringToFront = useCallback(() => {
    const id = selectedId;
    if (!id) return;
    applyShapes((prev) => {
      const maxZ = prev.reduce((m, s) => Math.max(m, s.zIndex || 0), 0);
      return prev.map((s) => (s.id === id ? { ...s, zIndex: maxZ + 1 } : s));
    });
  }, [selectedId, applyShapes]);

  const sendToBack = useCallback(() => {
    const id = selectedId;
    if (!id) return;
    applyShapes((prev) => {
      const minZ = prev.reduce((m, s) => Math.min(m, s.zIndex || 0), 0);
      return prev.map((s) => (s.id === id ? { ...s, zIndex: minZ - 1 } : s));
    });
  }, [selectedId, applyShapes]);

  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((meta && e.key.toLowerCase() === 'z' && e.shiftKey) || (meta && e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        redo();
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        applyShapes((prev) => prev.filter((s) => s.id !== selectedId));
        setSelectedId(null);
      } else if (e.key === 'Escape') {
        setSelectedId(null);
        setTool('select');
        setLinkingEntity(null);
      } else if (meta && e.key === ']' && selectedId) {
        e.preventDefault();
        bringToFront();
      } else if (meta && e.key === '[' && selectedId) {
        e.preventDefault();
        sendToBack();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, undo, redo, applyShapes, bringToFront, sendToBack]);

  const backgroundUrl = useMemo(() => {
    if (canvas.useFloorPlanAsBackground !== false && floor?.floorPlan?.url) return floor.floorPlan.url;
    return canvas.backgroundImage?.url || null;
  }, [canvas, floor]);

  const selectedShape = shapes.find((s) => s.id === selectedId) || null;

  /** Entity IDs already drawn on the canvas (draft shapes). */
  const placedEntityIds = useMemo(() => {
    const ids = new Set();
    for (const s of shapes) {
      if (s.entityId) ids.add(s.entityId.toString());
    }
    return ids;
  }, [shapes]);

  /** Unplaced list filtered against current canvas — prevents placing the same space twice. */
  const placeable = useMemo(() => {
    const entities = placeableData?.entities || { offices: [], coworkings: [], seats: [], meetingRooms: [] };
    const filterOut = (list) => (list || []).filter((e) => !placedEntityIds.has(e._id.toString()));
    return {
      offices: filterOut(entities.offices),
      coworkings: filterOut(entities.coworkings),
      seats: filterOut(entities.seats),
      meetingRooms: filterOut(entities.meetingRooms),
    };
  }, [placeableData, placedEntityIds]);

  const allEntities = useMemo(() => {
    const entities = placeableData?.entities || { offices: [], coworkings: [], seats: [], meetingRooms: [] };
    const markPlaced = (list) => (list || []).map((e) => ({
      ...e,
      isPlaced: placedEntityIds.has(e._id.toString()),
    }));
    return {
      offices: markPlaced(entities.offices),
      seats: markPlaced(entities.seats),
    };
  }, [placeableData, placedEntityIds]);

  const invalidSeatIds = useMemo(() => getInvalidSeatIds(shapes), [shapes]);
  const seatValidation = useMemo(() => validateSeatPlacements(shapes), [shapes]);

  const ensureLayout = async () => {
    if (layoutExistsRef.current) return;
    await createMutation.mutateAsync({
      floorId,
      data: { shapes: shapesRef.current, canvas: canvasRef.current },
    });
    layoutExistsRef.current = true;
  };

  const handleExplicitSave = async () => {
    if (!seatValidation.valid) {
      NotifyError(seatValidation.message);
      return;
    }
    await ensureLayout();
    saveMutation.mutate({ floorId, data: { shapes: shapesRef.current, canvas: canvasRef.current } });
    refetchPlaceable();
  };

  const handlePublish = async () => {
    if (!seatValidation.valid) {
      NotifyError(seatValidation.message);
      return;
    }
    await ensureLayout();
    await saveMutation.mutateAsync({ floorId, data: { shapes: shapesRef.current, canvas: canvasRef.current } });
    publishMutation.mutate({ floorId }, {
      onSuccess: () => onDone?.(),
    });
  };

  const isEntityAlreadyPlaced = (entityId) => {
    if (!entityId) return false;
    return shapesRef.current.some((s) => s.entityId && s.entityId.toString() === entityId.toString());
  };

  const placeNewShape = (pointer, toolDef, entity = null) => {
    // Linked spaces must come from an entity, and only once
    if (LINKABLE_TYPES.has(toolDef.elementType)) {
      if (!entity?._id) {
        NotifyError('Pick a space from the unplaced list to place it.');
        return;
      }
      if (isEntityAlreadyPlaced(entity._id)) {
        NotifyError(`${entity.label || 'This space'} is already on the canvas.`);
        setLinkingEntity(null);
        setTool('select');
        return;
      }
    }

    const grid = canvas.gridSize || 20;
    const x = snapToGrid(pointer.x, grid);
    const y = snapToGrid(pointer.y, grid);
    const defaults = ELEMENT_DEFAULT_STYLE[toolDef.elementType] || {};
    const maxZ = shapesRef.current.reduce((m, s) => Math.max(m, s.zIndex || 0), 0);

    const shape = {
      id: createShapeId(),
      shapeType: toolDef.shapeType,
      elementType: toolDef.elementType,
      x,
      y,
      width: toolDef.w,
      height: toolDef.h || toolDef.w,
      rotation: 0,
      points: toolDef.shapeType === 'line' ? [0, 0, toolDef.w, 0] : undefined,
      text: entity?.label
        || (toolDef.elementType === 'stairs' ? 'Stairs'
          : toolDef.elementType === 'area' ? (toolDef.text || AREA_PRESETS.find((p) => p.value === (toolDef.facilityKind || areaKind))?.label || 'Area')
            : undefined),
      facilityKind: toolDef.elementType === 'area' ? (toolDef.facilityKind || areaKind) : undefined,
      entityType: entity ? entity.entityType : undefined,
      entityId: entity?._id || undefined,
      style: { ...defaults },
      zIndex: maxZ + 1,
      locked: false,
    };

    if (toolDef.elementType === 'wall') {
      shape.points = [0, 0, toolDef.w || 120, 0];
      shape.width = toolDef.w || 120;
      shape.height = 0;
    }

    applyShapes((prev) => [...prev, shape]);
    setSelectedId(shape.id);
    setTool('select');
    setLinkingEntity(null);

    if (toolDef.elementType === 'seat' && !isSeatOnSharedArea(shape, [...shapesRef.current, shape])) {
      NotifyError('Seats must be placed on a shared (coworking) area.');
    }
  };

  const handleStageClick = (e) => {
    const stage = e.target.getStage();
    const pointer = stage.getRelativePointerPosition();
    if (!pointer) return;

    if (linkingEntity) {
      if (isEntityAlreadyPlaced(linkingEntity._id)) {
        NotifyError(`${linkingEntity.label} is already on the canvas.`);
        setLinkingEntity(null);
        return;
      }
      const defaults = ENTITY_PLACE_DEFAULTS[linkingEntity.entityType] || ENTITY_PLACE_DEFAULTS.office;
      placeNewShape(pointer, {
        elementType: linkingEntity.entityType,
        shapeType: defaults.shapeType,
        w: defaults.w,
        h: defaults.h,
      }, linkingEntity);
      return;
    }

    if (tool === 'select') {
      setSelectedId(null);
      return;
    }

    const toolDef = TOOLS.find((t) => t.id === tool);
    if (!toolDef || toolDef.id === 'select') return;
    if (!STRUCTURAL_TYPES.has(toolDef.elementType)) return;
    if (toolDef.elementType === 'area') {
      const preset = AREA_PRESETS.find((p) => p.value === areaKind) || AREA_PRESETS[0];
      placeNewShape(pointer, {
        ...toolDef,
        facilityKind: preset.value,
        text: preset.label,
      });
      return;
    }
    placeNewShape(pointer, toolDef);
  };

  const handleShapeDragEnd = (shape, e) => {
    const grid = canvas.gridSize || 20;
    const node = e.target;
    const x = snapToGrid(node.x(), grid);
    const y = snapToGrid(node.y(), grid);
    node.position({ x, y });
    applyShapes((prev) => {
      const next = prev.map((s) => (s.id === shape.id ? { ...s, x, y } : s));
      if (shape.elementType === 'seat') {
        const moved = next.find((s) => s.id === shape.id);
        if (moved && !isSeatOnSharedArea(moved, next)) {
          NotifyError('Seats must stay on a shared (coworking) area.');
        }
      }
      return next;
    });
  };

  const handleShapeTransformEnd = (shape, e) => {
    if (shape.elementType === 'wall') return;
    const node = e.target;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);
    const grid = canvas.gridSize || 20;
    const updatedShape = {
      ...shape,
      x: snapToGrid(node.x(), grid),
      y: snapToGrid(node.y(), grid),
      rotation: node.rotation(),
      width: Math.max(8, snapToGrid((shape.width || 40) * scaleX, grid) || 8),
      height: Math.max(8, snapToGrid((shape.height || 40) * (shape.elementType === 'door' ? scaleX : scaleY), grid) || 8),
    };
    // Doors keep roughly square swing radius
    if (shape.elementType === 'door') {
      updatedShape.height = updatedShape.width;
    }
    applyShapes((prev) => {
      const nextShapes = prev.map((s) => (s.id === shape.id ? updatedShape : s));
      if (shape.elementType === 'seat') {
        const moved = nextShapes.find((s) => s.id === shape.id);
        if (moved && !isSeatOnSharedArea(moved, nextShapes)) {
          NotifyError('Seats must stay on a shared (coworking) area.');
        }
      }
      return nextShapes;
    });
  };

  /** Resize wall length via orange endpoint handles (thickness is fixed). */
  const handleWallEndpointDrag = useCallback((shape, endpointIndex, localPos, { live } = {}) => {
    const grid = canvasRef.current.gridSize || 20;
    const pts = [...getWallPoints(shape)];
    const snapped = {
      x: snapToGrid(localPos.x, grid),
      y: snapToGrid(localPos.y, grid),
    };
    pts[endpointIndex * 2] = snapped.x;
    pts[endpointIndex * 2 + 1] = snapped.y;

    // Normalize so first point is 0,0 and position carries the offset
    const dx = pts[0];
    const dy = pts[1];
    const normalized = [0, 0, pts[2] - dx, pts[3] - dy];
    const length = Math.hypot(normalized[2], normalized[3]);
    if (length < 8) return;

    const next = {
      ...shape,
      x: (shape.x || 0) + dx,
      y: (shape.y || 0) + dy,
      points: normalized,
      width: Math.round(length),
      height: 0,
    };

    applyShapes(
      (prev) => prev.map((s) => (s.id === shape.id ? next : s)),
      { record: !live }
    );
  }, [applyShapes]);

  const updateSelected = (patch) => {
    if (!selectedId) return;
    applyShapes((prev) => prev.map((s) => (s.id === selectedId ? { ...s, ...patch } : s)));
  };

  const updateCanvasSize = (patch) => {
    const next = {
      ...canvas,
      ...patch,
      width: Math.max(400, Number(patch.width ?? canvas.width) || 2000),
      height: Math.max(400, Number(patch.height ?? canvas.height) || 1400),
    };
    setCanvas(next);
    if (layoutExistsRef.current) scheduleAutosave(shapesRef.current, next);
  };

  const onViewportResizeStart = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = viewportHeight;
    const onMove = (ev) => {
      const delta = ev.clientY - startY;
      setViewportHeight(Math.min(MAX_VIEWPORT_H, Math.max(MIN_VIEWPORT_H, startH + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (floorLoading || layoutLoading || !initialized) {
    return (
      <div className="flex justify-center py-20">
        <Spinner className="h-7 w-7" />
      </div>
    );
  }

  const isDraft = layout?.publishStatus !== 'published';
  const isSaving = updateMutation.isLoading || saveMutation.isLoading;

  return (
    <div className="flex h-[calc(100vh-7rem)] min-h-[560px] flex-col gap-0">
      {/* Header */}
      <header className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 pb-3">
        <div className="flex min-w-0 items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900"
              title="Back"
            >
              <ArrowLeftIcon size={16} />
            </button>
          )}
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <h1 className="truncate text-base font-medium text-gray-900">
                Floor {floor?.number}
                {floor?.floorCode ? (
                  <span className="font-normal text-gray-500"> · {floor.floorCode}</span>
                ) : null}
              </h1>
              <span className={`text-[11px] uppercase tracking-wider ${isDraft ? 'text-amber-700' : 'text-emerald-700'}`}>
                {isDraft ? 'Draft' : 'Published'}
              </span>
              {isSaving && <span className="text-[11px] text-gray-400">Saving…</span>}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <ActionBtn onClick={undo} disabled={historyIndex <= 0}><Undo2Icon size={14} className="inline mr-1" />Undo</ActionBtn>
          <ActionBtn onClick={redo} disabled={historyIndex >= history.length - 1}><Redo2Icon size={14} className="inline mr-1" />Redo</ActionBtn>
          <div className="mx-1 h-4 w-px bg-gray-200" />
          <div className="relative">
            <ActionBtn onClick={() => setExportOpen((v) => !v)}>
              <DownloadIcon size={14} className="inline mr-1" />Export
            </ActionBtn>
            {exportOpen && (
              <div className="absolute right-0 z-20 mt-1 w-40 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-sm">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    exportStageAsPng(stageRef.current, {
                      filename: `floor-plan-${floor?.floorCode || floorId}.png`,
                    });
                    setExportOpen(false);
                  }}
                >
                  <FileImageIcon size={14} /> PNG image
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    exportStageAsPdf(stageRef.current, {
                      filename: `floor-plan-${floor?.floorCode || floorId}`,
                      title: `Floor ${floor?.number} (${floor?.floorCode || ''})`,
                    });
                    setExportOpen(false);
                  }}
                >
                  <FileTextIcon size={14} /> PDF
                </button>
              </div>
            )}
          </div>
          <div className="mx-1 h-4 w-px bg-gray-200" />
          <ActionBtn onClick={handleExplicitSave} disabled={saveMutation.isLoading || !seatValidation.valid}>
            <SaveIcon size={14} className="inline mr-1" />Save
          </ActionBtn>
          <ActionBtn primary onClick={handlePublish} disabled={publishMutation.isLoading || !seatValidation.valid}>
            Publish
          </ActionBtn>
        </div>
      </header>

      {/* Toolbar */}
      <div className="flex shrink-0 flex-wrap items-center gap-0 border-b border-gray-100 py-1.5">
        {TOOLS.map((t, i) => {
          const Icon = t.icon;
          const active = tool === t.id && !linkingEntity;
          return (
            <React.Fragment key={t.id}>
              {i === 1 && <div className="mx-1 h-4 w-px bg-gray-200" />}
              <ToolBtn
                active={active}
                title={t.label}
                onClick={() => { setTool(t.id); setLinkingEntity(null); }}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{t.label}</span>
              </ToolBtn>
            </React.Fragment>
          );
        })}
        <div className="mx-1 h-4 w-px bg-gray-200" />
        <ToolBtn title="Bring to front" disabled={!selectedId} onClick={bringToFront}>
          <BringToFrontIcon size={14} />
          <span className="hidden lg:inline">Front</span>
        </ToolBtn>
        <ToolBtn title="Send to back" disabled={!selectedId} onClick={sendToBack}>
          <SendToBackIcon size={14} />
          <span className="hidden lg:inline">Back</span>
        </ToolBtn>
        <ToolBtn
          danger
          title="Delete"
          disabled={!selectedId}
          onClick={() => {
            if (!selectedId) return;
            applyShapes((prev) => prev.filter((s) => s.id !== selectedId));
            setSelectedId(null);
          }}
        >
          <EraserIcon size={14} />
          <span className="hidden lg:inline">Delete</span>
        </ToolBtn>
        {tool === 'area' && (
          <div className="ml-3 flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">Type</span>
            <PlainSelect
              value={areaKind}
              onChange={(e) => setAreaKind(e.target.value)}
              className="w-36"
            >
              {AREA_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </PlainSelect>
          </div>
        )}
        {linkingEntity && (
          <p className="ml-auto text-xs text-gray-500">
            Place <span className="font-medium text-gray-800">{linkingEntity.label}</span> — click the canvas
          </p>
        )}
      </div>

      {/* Workspace */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[200px_minmax(0,1fr)_220px]">
        {/* Unplaced / All entities */}
        <aside className="flex min-h-0 flex-col border-r border-gray-100 pr-3 pt-3 max-lg:max-h-56 max-lg:border-r-0 max-lg:border-b max-lg:pb-3">
          <div className="mb-2 flex gap-1">
            {[
              { id: 'unplaced', label: 'Unplaced' },
              { id: 'all', label: 'All' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setEntityListTab(tab.id)}
                className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider transition-colors ${entityListTab === tab.id
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-400 hover:bg-gray-100'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            {entityListTab === 'unplaced' ? (
              [
                { key: 'offices', addLabel: 'Office', onAdd: () => setOfficeModalOpen(true) },
                { key: 'coworkings', addLabel: 'Coworking', onAdd: () => setCoworkingModalOpen(true) },
                { key: 'seats', addLabel: 'Seat', onAdd: () => setSeatModalOpen(true) },
                { key: 'meetingRooms', addLabel: null, onAdd: null },
              ].map(({ key, addLabel, onAdd }) => (
                <div key={key}>
                  <div className="mb-1 flex items-center justify-between gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-400">
                      {SECTION_LABELS[key]}
                    </span>
                    {onAdd && (
                      <button
                        type="button"
                        onClick={onAdd}
                        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        title={`Add ${addLabel}`}
                      >
                        <PlusIcon size={11} /> Add
                      </button>
                    )}
                  </div>
                  <ul className="space-y-0.5">
                    {(placeable[key] || []).map((entity) => (
                      <li key={entity._id}>
                        <button
                          type="button"
                          onClick={() => {
                            setLinkingEntity(entity);
                            setTool('select');
                          }}
                          className={`w-full truncate rounded-lg px-1.5 py-1 text-left text-xs transition-colors ${linkingEntity?._id === entity._id
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          {entity.label}
                        </button>
                      </li>
                    ))}
                    {!(placeable[key] || []).length && (
                      <li className="px-1.5 py-0.5 text-[11px] text-gray-300">None</li>
                    )}
                  </ul>
                </div>
              ))
            ) : (
              [
                { key: 'offices', addLabel: 'Office', onAdd: () => setOfficeModalOpen(true) },
                { key: 'seats', addLabel: 'Seat', onAdd: () => setSeatModalOpen(true) },
              ].map(({ key, addLabel, onAdd }) => (
                <div key={key}>
                  <div className="mb-1 flex items-center justify-between gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-gray-400">
                      {SECTION_LABELS[key]}
                    </span>
                    {onAdd && (
                      <button
                        type="button"
                        onClick={onAdd}
                        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        title={`Add ${addLabel}`}
                      >
                        <PlusIcon size={11} /> Add
                      </button>
                    )}
                  </div>
                  <ul className="space-y-0.5">
                    {(allEntities[key] || []).map((entity) => (
                      <li key={entity._id}>
                        <button
                          type="button"
                          disabled={entity.isPlaced}
                          onClick={() => {
                            if (entity.isPlaced) return;
                            setLinkingEntity(entity);
                            setTool('select');
                          }}
                          className={`w-full truncate rounded-lg px-1.5 py-1 text-left text-xs transition-colors ${entity.isPlaced
                            ? 'text-gray-300 cursor-default'
                            : linkingEntity?._id === entity._id
                              ? 'bg-gray-900 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          {entity.label}
                          {entity.isPlaced && <span className="ml-1 text-[10px]">· placed</span>}
                        </button>
                      </li>
                    ))}
                    {!(allEntities[key] || []).length && (
                      <li className="px-1.5 py-0.5 text-[11px] text-gray-300">None</li>
                    )}
                  </ul>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Canvas */}
        <div className="flex min-h-0 min-w-0 flex-col pt-3 lg:px-3">
          {!seatValidation.valid && (
            <div className="mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {seatValidation.message}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
            <FloorPlanStage
              stageRef={stageRef}
              width={stageWidth}
              height={viewportHeight}
              canvas={canvas}
              shapes={shapes}
              backgroundUrl={backgroundUrl}
              colorByOccupancy={false}
              selectedId={selectedId}
              invalidSeatIds={[...invalidSeatIds]}
              editable={tool === 'select' && !linkingEntity}
              onSelectShape={(shape) => {
                if (tool !== 'select' || linkingEntity) return;
                setSelectedId(shape.id);
              }}
              onStageClick={handleStageClick}
              onShapeDragEnd={handleShapeDragEnd}
              onShapeTransformEnd={handleShapeTransformEnd}
              onWallEndpointDrag={handleWallEndpointDrag}
            />
          </div>
          <div
            role="separator"
            aria-label="Resize canvas view"
            onMouseDown={onViewportResizeStart}
            className="flex h-2 cursor-ns-resize items-center justify-center rounded-b-xl border border-t-0 border-gray-200 hover:bg-gray-100"
          >
            <span className="h-0.5 w-8 rounded-full bg-gray-300" />
          </div>
        </div>

        {/* Properties */}
        <aside className="flex min-h-0 flex-col border-l border-gray-100 pl-3 pt-3 max-lg:border-l-0 max-lg:border-t max-lg:pt-3">
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-gray-400">
            Properties
          </h2>
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pb-4">
            {!selectedShape ? (
              <p className="text-xs text-gray-400">Select a shape on the canvas.</p>
            ) : (
              <div className="space-y-3">
                <Field label="Type">
                  <span className="text-sm capitalize text-gray-800">
                    {selectedShape.elementType?.replace(/_/g, ' ')}
                  </span>
                </Field>
                {selectedShape.entityId && (
                  <Field label="Code">
                    <span className="text-sm text-gray-800">{selectedShape.text || '—'}</span>
                  </Field>
                )}
                {selectedShape.elementType === 'area' && (
                  <Field label="Kind">
                    <PlainSelect
                      value={selectedShape.facilityKind || 'other'}
                      onChange={(e) => {
                        const v = e.target.value;
                        const preset = AREA_PRESETS.find((p) => p.value === v);
                        updateSelected({ facilityKind: v, text: preset?.label || selectedShape.text });
                      }}
                    >
                      {AREA_PRESETS.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </PlainSelect>
                  </Field>
                )}
                {(selectedShape.elementType === 'area' || selectedShape.elementType === 'stairs') && (
                  <Field label="Label">
                    <PlainInput
                      value={selectedShape.text || ''}
                      onChange={(e) => updateSelected({ text: e.target.value })}
                    />
                  </Field>
                )}
                {selectedShape.elementType === 'wall' ? (
                  <>
                    <Field label="Len">
                      <span className="text-sm text-gray-800">{wallLength(selectedShape)} px</span>
                    </Field>
                    <p className="text-[11px] leading-relaxed text-gray-400">
                      Drag orange endpoints to change length. Thickness is fixed.
                    </p>
                    <Field label="X">
                      <PlainInput type="number" value={selectedShape.x || 0} onChange={(e) => updateSelected({ x: Number(e.target.value) })} />
                    </Field>
                    <Field label="Y">
                      <PlainInput type="number" value={selectedShape.y || 0} onChange={(e) => updateSelected({ y: Number(e.target.value) })} />
                    </Field>
                  </>
                ) : (
                  <>
                    <Field label="X">
                      <PlainInput type="number" value={selectedShape.x || 0} onChange={(e) => updateSelected({ x: Number(e.target.value) })} />
                    </Field>
                    <Field label="Y">
                      <PlainInput type="number" value={selectedShape.y || 0} onChange={(e) => updateSelected({ y: Number(e.target.value) })} />
                    </Field>
                    <Field label="W">
                      <PlainInput type="number" value={selectedShape.width || 0} onChange={(e) => updateSelected({ width: Number(e.target.value) })} />
                    </Field>
                    <Field label={selectedShape.elementType === 'door' ? 'Swing' : 'H'}>
                      <PlainInput
                        type="number"
                        value={selectedShape.height || 0}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          if (selectedShape.elementType === 'door') updateSelected({ width: v, height: v });
                          else updateSelected({ height: v });
                        }}
                      />
                    </Field>
                    <Field label="Rot">
                      <PlainInput type="number" value={selectedShape.rotation || 0} onChange={(e) => updateSelected({ rotation: Number(e.target.value) })} />
                    </Field>
                  </>
                )}
                <div className="flex gap-1 pt-1">
                  <ActionBtn onClick={bringToFront}>Front</ActionBtn>
                  <ActionBtn onClick={sendToBack}>Back</ActionBtn>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    className="rounded-sm border-gray-300"
                    checked={!!selectedShape.locked}
                    onChange={(e) => updateSelected({ locked: e.target.checked })}
                  />
                  Locked
                </label>
              </div>
            )}

            <div className="border-t border-gray-100 pt-4 space-y-3">
              <h3 className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                Canvas
              </h3>
              <Field label="Width">
                <PlainInput
                  type="number"
                  value={canvas.width || 2000}
                  onChange={(e) => updateCanvasSize({ width: e.target.value })}
                />
              </Field>
              <Field label="Height">
                <PlainInput
                  type="number"
                  value={canvas.height || 1400}
                  onChange={(e) => updateCanvasSize({ height: e.target.value })}
                />
              </Field>
              <Field label="Grid">
                <PlainInput
                  type="number"
                  value={canvas.gridSize || 20}
                  onChange={(e) => setCanvas({ ...canvas, gridSize: Number(e.target.value) || 20 })}
                />
              </Field>
              <label className="flex items-start gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded-sm border-gray-300"
                  checked={canvas.useFloorPlanAsBackground !== false}
                  onChange={(e) => {
                    const next = { ...canvas, useFloorPlanAsBackground: e.target.checked };
                    setCanvas(next);
                    if (layoutExistsRef.current) scheduleAutosave(shapesRef.current, next);
                  }}
                />
                <span>Floor plan as background{floor?.floorPlan?.url ? '' : ' (none uploaded)'}</span>
              </label>
            </div>
          </div>
        </aside>
      </div>

      <OfficeCrudModal
        isOpen={officeModalOpen}
        onClose={() => setOfficeModalOpen(false)}
        defaultFloorId={floorId}
        onSuccess={() => refetchPlaceable()}
      />
      <CoworkingCrudModal
        isOpen={coworkingModalOpen}
        onClose={() => setCoworkingModalOpen(false)}
        defaultFloorId={floorId}
        onSuccess={() => refetchPlaceable()}
      />
      <SeatCrudModal
        isOpen={seatModalOpen}
        onClose={() => setSeatModalOpen(false)}
        defaultFloorId={floorId}
        onSuccess={() => refetchPlaceable()}
      />
    </div>
  );
};

export default FloorPlanEditor;
